/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* Variant‑specific description toggle for Symmetry  

document.addEventListener('DOMContentLoaded', function () {
  const descs = document.querySelectorAll('#variant-descriptions .variant-desc');
  const radios = document.querySelectorAll('input[type="radio"][name^="product-form-template"]');

  if (!radios.length || !descs.length) return;

  function showDesc(value) {
    descs.forEach(desc => {
      if (desc.getAttribute('data-value') === value.toLowerCase()) {
        desc.style.display = 'block';
      } else {
        desc.style.display = 'none';
      }
    });
  }

  // On page load — find selected radio
  const selected = Array.from(radios).find(r => r.checked);
  if (selected) showDesc(selected.value);

  // On change
  radios.forEach(radio => {
    radio.addEventListener('change', function () {
      if (this.checked) showDesc(this.value);
    });
  });
});

*/



/*

document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = document.querySelectorAll('input[name^="product-form-template"]');

  function selectDefaultSize(type) {
    const defaults = {
      "Classic": "200g (8.8oz)",
      "Fancy": "200g (8.8oz) x 2"
    };

    const desiredValue = defaults[type];

    sizeRadios.forEach(radio => {
      if (radio.value === desiredValue) {
        radio.checked = true;

        // Force Shopify update
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      selectDefaultSize(radio.value);
    });
  });

  // Initial selection (only if Classic is selected by default)
  const defaultSelected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked');
  if (defaultSelected) {
    selectDefaultSize("Classic");
  }
});

*/

/*
document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = document.querySelectorAll('input[name^="product-form-template"]');

  const defaults = {
    "Classic": "200g (8.8oz)",
    "Fancy": "200g (8.8oz) x 2"
  };

  function selectDefaultSize(type) {
    const desiredValue = defaults[type];

    sizeRadios.forEach(radio => {
      if (radio.value === desiredValue) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function updateDescriptions(selectedType) {
    document.querySelectorAll('.variant-desc').forEach(desc => {
      desc.style.display = desc.dataset.variantId === selectedType ? 'block' : 'none';
    });
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      const type = radio.value;
      selectDefaultSize(type);
      updateDescriptions(type); // Add this line
    });
  });

  // Run on load if Classic is default
  const defaultSelected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked');
  if (defaultSelected) {
    selectDefaultSize("Classic");
    updateDescriptions("Classic"); // Add this too
  }
});

*/

/*

document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = document.querySelectorAll('input[name^="product-form-template"]');

  function selectDefaultSize(type) {
    const defaults = {
      "Classic": "200g (8.8oz)",  // exact value of your size radio for Classic
      "Fancy": "200g (8.8oz) x 2" // exact value of your size radio for Fancy
    };

    const desiredValue = defaults[type];

    sizeRadios.forEach(radio => {
      if (radio.value === desiredValue) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function showDescription(type) {
    document.querySelectorAll('.variant-desc').forEach(desc => {
      const value = desc.getAttribute('data-value');
      desc.style.display = (value === type.toLowerCase()) ? 'block' : 'none';
    });
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      const type = radio.value;
      selectDefaultSize(type);
      showDescription(type);
    });
  });

  const defaultSelected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked');
  if (defaultSelected) {
    selectDefaultSize("Classic");
    showDescription("Classic");
  }
});

*/

/*
document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = document.querySelectorAll('input[name^="product-form-template"][type="radio"]');

  const typeMap = {
    "Classic": [
      "200g (8.8oz)",
      "400g (14oz)",
      "600g (21oz)",
      "800g (28oz)",
      "1kg (2.2lb)"
    ],
    "Fancy": [
      "200g (8.8oz) x 2"
    ]
  };

  function selectDefaultSize(type) {
    const defaultValue = typeMap[type][0]; // First size in list as default
    sizeRadios.forEach(radio => {
      if (radio.value === defaultValue) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function showDescription(type) {
    document.querySelectorAll('.variant-desc').forEach(desc => {
      const value = desc.getAttribute('data-value');
      desc.style.display = (value === type.toLowerCase()) ? 'block' : 'none';
    });
  }

  function filterSizeOptions(type) {
    sizeRadios.forEach(radio => {
      const wrapper = radio.closest('label');
      if (!wrapper) return;

      const isAllowed = typeMap[type].includes(radio.value);
      wrapper.style.display = isAllowed ? 'inline-block' : 'none';
    });
  }

  function handleVariantChange(type) {
    selectDefaultSize(type);
    showDescription(type);
    filterSizeOptions(type);
  }

  // Attach event listeners
  typeRadios.forEach(radio => {
    radio.addEventListener('change', () => handleVariantChange(radio.value));
  });

  // On page load, run setup for selected variant
  const selected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked') ||
                   document.querySelector('input[name^="product-form-template"][value="Fancy"]:checked');

  if (selected) {
    handleVariantChange(selected.value);
  }
});

*/

/*
document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = document.querySelectorAll('input[name^="product-form-template"][type="radio"]');

  const typeMap = {
    "Classic": [
      "200g (8.8oz)",
      "400g (14oz)",
      "600g (21oz)",
      "800g (28oz)",
      "1kg (2.2lb)"
    ],
    "Fancy": [
      "200g (8.8oz) x 2"
    ]
  };

  function selectDefaultSize(type) {
    const defaultValue = typeMap[type][0];
    sizeRadios.forEach(radio => {
      if (radio.value === defaultValue) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function showDescription(type) {
    document.querySelectorAll('.variant-desc').forEach(desc => {
      const value = desc.getAttribute('data-value');
      desc.style.display = (value === type.toLowerCase()) ? 'block' : 'none';
    });
  }

  function filterSizeOptions(type) {
    const allowed = typeMap[type];

    sizeRadios.forEach(radio => {
      const label = radio.closest('label');
      if (!label) return;

      if (allowed.includes(radio.value)) {
        label.style.display = 'inline-block';
      } else {
        label.style.display = 'none';

        // Uncheck hidden radios to avoid invisible selection
        if (radio.checked) {
          radio.checked = false;
        }
      }
    });
  }

  function handleVariantChange(type) {
    filterSizeOptions(type);
    selectDefaultSize(type);
    showDescription(type);
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      handleVariantChange(this.value);
    });
  });

  // Initial run
  const defaultSelected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked') ||
                          document.querySelector('input[name^="product-form-template"][value="Fancy"]:checked');
  if (defaultSelected) {
    handleVariantChange(defaultSelected.value);
  }
});

*/

/*

document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = document.querySelectorAll('input[name^="product-form-template"]');
  const allSizeOptions = document.querySelectorAll('label.opt-label');

  const defaults = {
    "Classic": "200g (8.8oz)",
    "Fancy": "200g (8.8oz) x 2"
  };

  const allowedSizes = {
    "Classic": ["200g (8.8oz)", "400g (14oz)", "600g (21oz)", "800g (28oz)", "1kg (2.2lb)"],
    "Fancy": ["200g (8.8oz) x 2"]
  };

  function selectDefaultSize(type) {
    const defaultValue = defaults[type];

    sizeRadios.forEach(radio => {
      const label = radio.closest('label');
      const isAllowed = allowedSizes[type].includes(radio.value);

      // Show/hide size options
      if (label) {
        label.style.display = isAllowed ? '' : 'none';
      }

      // Select the default size
      if (radio.value === defaultValue) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function updateDescription(type) {
    const allDescs = document.querySelectorAll('.variant-desc');
    allDescs.forEach(desc => {
      desc.style.display = desc.getAttribute('data-value').toLowerCase() === type.toLowerCase() ? 'block' : 'none';
    });
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      selectDefaultSize(radio.value);
      updateDescription(radio.value);
    });
  });

  // Initial load (if Classic is selected by default)
  const defaultSelected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked');
  const defaultFancy = document.querySelector('input[name^="product-form-template"][value="Fancy"]:checked');
  if (defaultSelected) {
    selectDefaultSize("Classic");
    updateDescription("Classic");
  } else if (defaultFancy) {
    selectDefaultSize("Fancy");
    updateDescription("Fancy");
  }
});

*/

/*
document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = document.querySelectorAll('input[name^="product-form-template"]');
  const allLabels = document.querySelectorAll('label.opt-label');

  const defaults = {
    "Classic": "200g (8.8oz)",
    "Fancy": "200g (8.8oz) x 2"
  };

  const showSizes = {
    "Classic": ["200g (8.8oz)", "400g (14oz)", "600g (21oz)", "800g (28oz)", "1kg (2.2lb)"],
    "Fancy": ["200g (8.8oz) x 2"]
  };

  function updateForVariant(type) {
    const allowed = showSizes[type];
    const defaultValue = defaults[type];

    sizeRadios.forEach(input => {
      const label = input.closest('label');
      const isAllowed = allowed.includes(input.value);

      if (label) {
        label.style.display = isAllowed ? '' : 'none';
      }

      if (input.value === defaultValue) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Show/hide descriptions
    const allDescs = document.querySelectorAll('.variant-desc');
    allDescs.forEach(desc => {
      const val = desc.getAttribute('data-value');
      desc.style.display = val.toLowerCase() === type.toLowerCase() ? 'block' : 'none';
    });
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', () => updateForVariant(radio.value));
  });

  // On page load: detect which type is selected and update accordingly
  const selectedTypeRadio = document.querySelector('input[name^="product-form-template"][value="Fancy"]:checked') ||
                            document.querySelector('input[name^="product-form-template"][value="Classic"]:checked');
  if (selectedTypeRadio) {
    updateForVariant(selectedTypeRadio.value);
  }
});

*/

/*
document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');

  const sizeRadios = document.querySelectorAll('input[name^="product-form-template"]');

  function selectDefaultSize(type) {
    const defaults = {
      "Classic": "200g (8.8oz)",
      "Fancy": "200g (8.8oz) x 2"
    };

    const allowedSizes = {
      "Classic": ["200g (8.8oz)", "400g (14oz)", "600g (21oz)", "800g (28oz)", "1kg (2.2lb)"],
      "Fancy": ["200g (8.8oz) x 2"]
    };

    const desiredValue = defaults[type];

    sizeRadios.forEach(radio => {
      const label = document.querySelector(`label[for="${radio.id}"]`);
      if (!label) return;

      if (allowedSizes[type].includes(radio.value)) {
        label.style.display = "";
      } else {
        label.style.display = "none";
      }

      // Select default radio button
      if (radio.value === desiredValue) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      selectDefaultSize(radio.value);
    });
  });

  const defaultSelected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked');
  if (defaultSelected) {
    selectDefaultSize("Classic");
  }
});

*/

/*

document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = Array.from(document.querySelectorAll('input[name^="product-form-template"]'))
    .filter(radio => !["Classic", "Fancy"].includes(radio.value)); // Exclude type radios

  function selectDefaultSize(type) {
    const defaults = {
      "Classic": "200g (8.8oz)",
      "Fancy": "200g (8.8oz) x 2"
    };

    const allowedSizes = {
      "Classic": ["200g (8.8oz)", "400g (14oz)", "600g (21oz)", "800g (28oz)", "1kg (2.2lb)"],
      "Fancy": ["200g (8.8oz) x 2"]
    };

    const desiredValue = defaults[type];

    sizeRadios.forEach(radio => {
      const label = document.querySelector(`label[for="${radio.id}"]`);
      if (!label) return;

      if (allowedSizes[type].includes(radio.value)) {
        label.style.display = "";
      } else {
        label.style.display = "none";
      }

      if (radio.value === desiredValue) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      selectDefaultSize(radio.value);
    });
  });

  const defaultSelected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked');
  if (defaultSelected) {
    selectDefaultSize("Classic");
  }
});

*/



document.addEventListener('DOMContentLoaded', function () {
  const typeRadios = document.querySelectorAll('input[name^="product-form-template"][value="Classic"], input[name^="product-form-template"][value="Fancy"]');
  const sizeRadios = Array.from(document.querySelectorAll('input[name^="product-form-template"]'))
    .filter(radio => !["Classic", "Fancy"].includes(radio.value)); // Exclude type radios

  function selectDefaultSize(type) {
    const defaults = {
      "Classic": "200g (8.8oz)",
      "Fancy": "200g (8.8oz) x 2"
    };

    const allowedSizes = {
      "Classic": ["200g (8.8oz)", "400g (14oz)", "600g (21oz)", "800g (28oz)", "1kg (2.2lb)"],
      "Fancy": ["200g (8.8oz) x 2"]
    };

    const desiredValue = defaults[type];

    sizeRadios.forEach(radio => {
      const label = document.querySelector(`label[for="${radio.id}"]`);
      if (!label) return;

      if (allowedSizes[type].includes(radio.value)) {
        label.style.display = "";
      } else {
        label.style.display = "none";
      }

      if (radio.value === desiredValue) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    updateDescription(type);
  }

  function updateDescription(type) {
    console.log(`Updating description for type: ${type}`); // Debug log
    document.querySelectorAll('.variant-desc').forEach(desc => {
      if (desc.getAttribute('data-value') === type.toLowerCase()) {
        desc.style.display = 'block';
      } else {
        desc.style.display = 'none';
      }
    });
  }

  typeRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      selectDefaultSize(radio.value);
    });
  });

  // Set the default selection on page load
  const defaultSelected = document.querySelector('input[name^="product-form-template"][value="Classic"]:checked, input[name^="product-form-template"][value="Fancy"]:checked');
  if (defaultSelected) {
    console.log(`Default type on load: ${defaultSelected.value}`); // Debug log
    selectDefaultSize(defaultSelected.value);
  } else {
    // Fallback: If no type is selected, default to "Fancy"
    console.log("No default type selected, falling back to Fancy"); // Debug log
    const fancyRadio = document.querySelector('input[name^="product-form-template"][value="Fancy"]');
    if (fancyRadio) {
      fancyRadio.checked = true;
      selectDefaultSize("Fancy");
    }
  }
});






































































































